/**
 * Offline Sync Manager
 * 
 * Queues failed write operations when the app is in offline mode.
 * When connection restores, flushOfflineQueue() replays them.
 * Auto-detects browser offline/online transitions.
 */

import { supabase } from '@/integrations/supabase/client';
import { safeSetItem, safeSessionSetItem } from '@/lib/safeStorage';

const QUEUE_KEY = 'aphenis_offline_queue';
const OFFLINE_FLAG = 'aphenis_offline_mode';
const ACTION_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

export type OfflineActionType =
  | 'inventory_update'
  | 'inventory_delete'
  | 'inventory_insert'
  | 'chest_collected'
  | 'send_message'
  | 'send_group_message'
  | 'backup_keys'
  | 'backup_data';

interface OfflineAction {
  type: OfflineActionType;
  payload: Record<string, any>;
  timestamp: number;
}

// ─── Offline flag (sessionStorage — resets on tab close) ──

export function isOfflineMode(): boolean {
  try { return sessionStorage.getItem(OFFLINE_FLAG) === 'true'; } catch { return false; }
}

export function setOfflineMode(val: boolean): void {
  try {
    if (val) safeSessionSetItem(OFFLINE_FLAG, 'true');
    else sessionStorage.removeItem(OFFLINE_FLAG);
  } catch {}
  // Notify listeners reactively
  window.dispatchEvent(new CustomEvent('offline-mode-change', { detail: { offline: val } }));
}

// ─── Queue management ─────────────────────────────────

function getQueue(): OfflineAction[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveQueue(queue: OfflineAction[]): void {
  try { safeSetItem(QUEUE_KEY, JSON.stringify(queue)); } catch {}
}

export function queueOfflineAction(type: OfflineActionType, payload: Record<string, any>): void {
  const queue = getQueue();
  queue.push({ type, payload, timestamp: Date.now() });
  saveQueue(queue);
  window.dispatchEvent(new CustomEvent('offline-queue-change'));
}

export function getOfflineQueueSize(): number {
  return getQueue().length;
}

// ─── Flush queue on reconnection ──────────────────────

export async function flushOfflineQueue(): Promise<number> {
  const queue = getQueue();
  if (queue.length === 0) return 0;

  const now = Date.now();
  let synced = 0;
  const failed: OfflineAction[] = [];

  for (const action of queue) {
    // Discard stale actions older than 24 hours
    if (now - action.timestamp > ACTION_TTL_MS) {
      console.warn('[OfflineSync] Discarding stale action:', action.type, 'age:', Math.round((now - action.timestamp) / 60000), 'min');
      continue;
    }
    try {
      const ok = await replayAction(action);
      if (ok) synced++;
      else failed.push(action);
    } catch {
      failed.push(action);
    }
  }

  saveQueue(failed);
  window.dispatchEvent(new CustomEvent('offline-queue-change'));
  return synced;
}

async function replayAction(action: OfflineAction): Promise<boolean> {
  const { type, payload } = action;

  switch (type) {
    case 'inventory_update': {
      const { secureInventoryUpdate } = await import('./secureApiClient');
      return await secureInventoryUpdate(payload.userUid, payload.itemId, payload.quantity, payload.petChatId || null);
    }
    case 'inventory_insert': {
      const { secureInventoryInsert } = await import('./secureApiClient');
      const result = await secureInventoryInsert(payload.userUid, payload.itemId, payload.itemType, payload.quantity, payload.petChatId || null);
      return result.success || result.code === '23505';
    }
    case 'inventory_delete': {
      const { secureInventoryDelete } = await import('./secureApiClient');
      return await secureInventoryDelete(payload.userUid, payload.itemId, payload.petChatId || null);
    }
    case 'chest_collected': {
      const { error } = await supabase.from('chest_completions')
        .insert([{ user_uid: payload.userUid, chest_id: payload.chestId, method: payload.method }]);
      return !error || error.code === '23505';
    }
    case 'send_message': {
      const { error } = await supabase.from('messages')
        .insert({ sender_uid: payload.senderUid, recipient_uid: payload.recipientUid, encrypted_content: payload.encryptedContent, nonce: payload.nonce, burn_after_view: payload.burnAfterView || false });
      return !error;
    }
    case 'send_group_message': {
      const { error } = await supabase.from('messages')
        .insert({ sender_uid: payload.senderUid, group_id: payload.groupId, encrypted_content: payload.encryptedContent, nonce: payload.nonce, burn_after_view: payload.burnAfterView || false });
      return !error;
    }
    case 'backup_keys': {
      const { secureUpdateUser } = await import('./secureApiClient');
      const updateData: Record<string, string> = { encrypted_keys_backup: payload.encryptedKeys };
      if (payload.encryptedUnlockCode) updateData.encrypted_unlock_code_backup = payload.encryptedUnlockCode;
      return await secureUpdateUser(payload.uid, updateData);
    }
    case 'backup_data': {
      const { secureUpdateUser } = await import('./secureApiClient');
      return await secureUpdateUser(payload.uid, { encrypted_data_backup: payload.encryptedData });
    }
    default:
      return true; // Unknown type — discard
  }
}

/** Check if user is eligible for offline mode (has stored identity) */
export function canEnterOfflineMode(): boolean {
  try {
    if (localStorage.getItem('aphenis_unlocked') !== 'true') return false;
    try { const r = localStorage.getItem('vaultmsg_uid'); if (!r || !JSON.parse(r)?.uid) return false; } catch { return false; }
    return !!localStorage.getItem('vaultmsg_encrypted_keys');
  } catch { return false; }
}

// ─── Auto-detection of browser offline/online ─────────

let _initialized = false;

export function initOfflineDetection(): void {
  if (_initialized) return;
  _initialized = true;

  window.addEventListener('offline', () => {
    console.log('[OfflineSync] Browser went offline');
    if (canEnterOfflineMode() && !isOfflineMode()) {
      setOfflineMode(true);
    }
  });

  window.addEventListener('online', () => {
    console.log('[OfflineSync] Browser came online');
    if (isOfflineMode()) {
      // Delay slightly to let network stabilize
      setTimeout(async () => {
        try {
          const { checkBackendHealth } = await import('@/lib/healthCheck');
          const healthy = await checkBackendHealth();
          if (healthy) {
            const synced = await flushOfflineQueue();
            setOfflineMode(false);
            const { toast } = await import('sonner');
            if (synced > 0) {
              toast.success(`Connection restored — ${synced} action${synced > 1 ? 's' : ''} synced ✓`);
            } else {
              toast.success('Connection restored ✓');
            }
          }
        } catch (e) {
          console.warn('[OfflineSync] Reconnection check failed:', e);
        }
      }, 2000);
    }
  });
}
